import React, { useMemo, Fragment } from 'react';
import { useTable } from 'chameleon/ui-stack/ui/base/table';
import CustomCell from 'chameleon/ui-stack/ui/assessments/CustomCell';
import Row from 'chameleon/ui-stack/ui/base/table/Row';
import { ScrollableContainer } from 'legacy-packages/ft-cb';
import dateTimeFormat from 'chameleon/ui-stack/utilities/date';

import {
  getStudentCASValue,
  CASFields,
  hasStudentAnyCAS,
} from 'chameleon/legacy/src/components/assignment/Assignment/components/AssignmentTable/ColumnCAS/utils';

const StudentSettingsPopoverContent = (props) => {
  const {
    assignment: {
      section_id,
      student_settings,
      timezone: assignmentTimezone,
      id: assignmentId,
    },
    studentsBySection,
    isSPP,
  } = props;
  const parentAssignment = props.assignment;

  const getHeaders = () => {
    const commonClassNames =
      '!font-normal text-gray-600 py-3 px-6 uppercase text-14';
    let headers = [
      {
        Header: 'Student',
        id: 'user',
        className: commonClassNames,
      },
      {
        Header: 'Start Date',
        id: 'starts_at',
        className: commonClassNames,
      },
      {
        Header: 'Due Date',
        id: 'due_at',
        className: commonClassNames,
      },
    ];

    if (!isSPP) {
      headers.push({
        Header: 'Timer',
        id: 'timer',
        className: `${commonClassNames} timer`,
      });
    }
    return headers;
  };

  const headers = getHeaders();

  const getCellId = (userId, fieldName) =>
    `${assignmentId}-${userId}-${fieldName}`;

  const getFormattedValue = ({ value, isCustomSetting, field }) => {
    const casIndicator = isCustomSetting ? '*' : '';
    let valueRendered = value ? value : '-';
    if (value && [CASFields.StartsAt, CASFields.DueAt].includes(field)) {
      valueRendered = dateTimeFormat(value, assignmentTimezone);
    }
    return `${valueRendered}${casIndicator}`;
  };

  const renderRow = (row) => {
    const { original: cas } = row || {};
    const studentDetails = getStudentDetails(cas.user_id);
    const { firstName, lastName } = studentDetails;
    const studentFullName = `${firstName} ${lastName}`;

    const startDate = getFormattedValue(
      getStudentCASValue({
        field: CASFields.StartsAt,
        parentAssignment,
        customSetting: cas,
      }),
    );
    const dueDate = getFormattedValue(
      getStudentCASValue({
        field: CASFields.DueAt,
        parentAssignment,
        customSetting: cas,
      }),
    );
    const timer = getFormattedValue(
      getStudentCASValue({
        field: CASFields.Timer,
        parentAssignment,
        customSetting: cas,
      }),
    );

    const commonClassNames = 'text-gray-900 !py-3 !px-6';
    const hideCell = isSPP;

    const cells = [
      CustomCell(
        getCellId(cas.user_id, 'name'),
        studentFullName,
        '!font-medium max-w-[200px] !px-6 !py-3 student-name',
      ),
      CustomCell(
        getCellId(cas.user_id, 'start'),
        startDate,
        `${commonClassNames} start-date`,
      ),
      CustomCell(
        getCellId(cas.user_id, 'due'),
        dueDate,
        `${commonClassNames} due-date`,
      ),
      CustomCell(
        getCellId(cas.user_id, 'timer'),
        timer,
        `${commonClassNames} timer`,
        hideCell,
      ),
    ];

    return (
      <Row
        a11yIgnoreEvents
        row={row}
        className="border-0"
        key={`${cas.user_id}`}
        isDisabled
      >
        <TableColumns cells={cells} />
      </Row>
    );
  };

  const getStudentDetails = (studentId) => {
    const studentDetails =
      (studentsBySection[section_id] || []).find(
        (student) => student.id === studentId,
      ) || {};

    return {
      firstName: studentDetails.firstName || '',
      lastName: studentDetails.lastName || '',
    };
  };

  const onlyStudentsWithCAS = student_settings.filter((customSetting) =>
    hasStudentAnyCAS({ assignment: parentAssignment, customSetting }),
  );

  const studentsWithDetails = onlyStudentsWithCAS.map((student) => {
    const studentDetails = getStudentDetails(student.user_id);
    return {
      ...student,
      ...studentDetails,
    };
  });

  const sortedStudentsSettings = useMemo(
    () =>
      [...studentsWithDetails].sort((a, b) => {
        const compareLastNames = a.lastName
          .toLowerCase()
          .localeCompare(b.lastName.toLowerCase());
        if (compareLastNames === 0) {
          return a.firstName
            .toLowerCase()
            .localeCompare(b.firstName.toLowerCase());
        }

        return compareLastNames;
      }),
    [student_settings],
  );

  const { Table, TableBody, TableColumns, TableHeader, tableItems } = useTable({
    data: sortedStudentsSettings || [],
    headers,
  });

  return (
    <Fragment>
      <ScrollableContainer autoHeight autoHeightMax={240}>
        <Table>
          <TableHeader />
          <TableBody>{tableItems.map((item) => renderRow(item))}</TableBody>
        </Table>
      </ScrollableContainer>
    </Fragment>
  );
};

export default StudentSettingsPopoverContent;
